import * as vp from 'vopay';
import { CardTabs } from 'vopay';

import { FunctionComponent, useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AccountContext from '../AccountContext';
import Details from '../Details';
import Balances from '../Balances';
import Billing from '../Billing/Billing';
import Users from '../Users/UsersContainer';
import TransactionsContainer from '../Transactions/TransactionsContainer';
import AgreementContainer from '../Agreement/AgreementContainer';
import SignersContainer from '../Signers/SignersContainer';
import SubAccounts from '../SubAccounts';

import { useTranslation } from 'react-i18next';
import useStorage from '@/hooks/useStorage';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import useFetchAgreementDetails from '@/hooks/useApi/useFetchAgreementDetails';
import useFetchSigningAuthorityInfo from '@/hooks/useApi/useFetchSigningAuthorityInfo';

import type { AccountsHandlers } from '../types';
import ApplicationPendingState from './ApplicationPendingState';
import styles from './AccountTabs.module.scss';

type AccountProps = {
  transactions: vp.AccountTransactionsMap;
  handlers: AccountsHandlers;
  isPending: boolean;
  onBoardingLink: string | null;
  hasValidOnboardingUrl: boolean | null;
  isOnboardingEmailAllowed: boolean;
  onCopy: () => void;
  onSendEmail: () => void;
};

type TabKey = 'agreement' | 'balances' | 'billing' | 'details' | 'signers' | 'subaccounts' | 'transactions' | 'users';

type FC = FunctionComponent<AccountProps>;

const AccountTabs: FC = ({
  transactions,
  handlers,
  isPending,
  onBoardingLink,
  hasValidOnboardingUrl,
  isOnboardingEmailAllowed,
  onCopy,
  onSendEmail,
}: AccountProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { details, balances } = useContext(AccountContext);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const { canAccessBillingTab } = useModuleFeatureFlag('accounts');

  const {
    signerCreation: { getSignerCreation, clearSignerCreation },
    accountToken: { storeAccountToken },
  } = useStorage();

  const storedSignerCreation = getSignerCreation();
  const { accountId } = useParams<{ accountId: string }>();
  const accountToken = details[accountId].token;
  const [agreementDetails, fetchAgreementDetails] = useFetchAgreementDetails({ bearer: accountToken });

  const [signingAuthorityInfo, fetchSigningAuthorityInfo] = useFetchSigningAuthorityInfo({
    bearer: accountToken || '',
  });

  const [isAddSignerToastVisible, setIsAddSignerToastVisible] = useState(false);
  const [subAccountList, setSubAccountList] = useState<vp.AccountBalance[]>([]);
  const [showSubAccountListTab, setShowSubAccountListTab] = useState(false);

  useEffect(() => {
    storeAccountToken(accountToken);
  }, [details, accountId, accountToken]);

  useEffect(() => {
    fetchAgreementDetails();
  }, []);

  useEffect(() => {
    fetchSigningAuthorityInfo();
  }, [accountId, accountToken]);

  useEffect(() => {
    if (storedSignerCreation === 'signerToast') {
      fetchSigningAuthorityInfo();
      setActiveTab('signers');

      setTimeout(() => {
        clearSignerCreation();
      }, 200);

      setIsAddSignerToastVisible(true);

      setTimeout(() => {
        setIsAddSignerToastVisible(false);
      }, 2000);
    } else if (storedSignerCreation === 'signerChange') {
      fetchSigningAuthorityInfo();
      setActiveTab('signers');

      setTimeout(() => {
        clearSignerCreation();
      }, 200);
    }
  }, [storedSignerCreation]);

  useEffect(() => {
    const list = getSubAccounts(accountId, balances);
    setSubAccountList(list);
    setShowSubAccountListTab(list.length > 0);
  }, [balances, accountId]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabKey);
  };

  const handleAgreementTab = () => {
    setActiveTab('agreement');
  };

  const handleSignersTab = () => {
    setActiveTab('signers');
  };

  const handleSignerLists = () => {
    fetchSigningAuthorityInfo();
  };

  const pendingState = (
    <ApplicationPendingState
      onBoardingLink={onBoardingLink}
      hasValidOnboardingUrl={hasValidOnboardingUrl}
      isOnboardingEmailAllowed={isOnboardingEmailAllowed}
      onCopy={onCopy}
      onSendEmail={onSendEmail}
    />
  );

  return (
    <CardTabs.Root value={activeTab} onValueChange={handleTabChange} className={styles.tabsRoot}>
      <CardTabs.List>
        <CardTabs.Trigger value="details">{t('details', { defaultValue: 'Details' })}</CardTabs.Trigger>

        {canAccessBillingTab && (
          <CardTabs.Trigger value="billing">{t('billing', { defaultValue: 'Billing' })}</CardTabs.Trigger>
        )}

        {showSubAccountListTab && (
          <CardTabs.Trigger value="subaccounts">{t('subaccounts', { defaultValue: 'Sub-Accounts' })}</CardTabs.Trigger>
        )}

        <CardTabs.Trigger value="users">{t('users', { defaultValue: 'Portal Users' })}</CardTabs.Trigger>
        <CardTabs.Trigger value="transactions">{t('transactions', { defaultValue: 'Transactions' })}</CardTabs.Trigger>
      </CardTabs.List>

      <CardTabs.Content value="details">
        {isPending ? (
          pendingState
        ) : (
          <Details details={details} agreementDetails={agreementDetails} onClick={handleAgreementTab} />
        )}
      </CardTabs.Content>

      <CardTabs.Content value="billing">{isPending ? pendingState : <Billing />}</CardTabs.Content>
      <CardTabs.Content value="balances">{isPending ? pendingState : <Balances />}</CardTabs.Content>

      <CardTabs.Content value="subaccounts">
        {isPending ? (
          pendingState
        ) : (
          <SubAccounts subAccountList={subAccountList} details={details} handlers={handlers} />
        )}
      </CardTabs.Content>

      <CardTabs.Content value="users">{isPending ? pendingState : <Users details={details} />}</CardTabs.Content>

      <CardTabs.Content value="transactions">
        {isPending ? (
          pendingState
        ) : (
          <TransactionsContainer defaultTransactions={transactions} details={details} handlers={handlers} />
        )}
      </CardTabs.Content>

      <CardTabs.Content value="agreement">
        {isPending ? (
          pendingState
        ) : (
          <AgreementContainer
            agreementDetails={agreementDetails}
            accountToken={accountToken}
            signingAuthorityInfo={signingAuthorityInfo}
            onClick={handleSignersTab}
          />
        )}
      </CardTabs.Content>

      <CardTabs.Content value="signers">
        {isPending ? (
          pendingState
        ) : (
          <SignersContainer
            agreementDetails={agreementDetails}
            accountToken={accountToken}
            isAddSignerToastVisible={isAddSignerToastVisible}
            signingAuthorityInfo={signingAuthorityInfo}
            handleSignerLists={handleSignerLists}
          />
        )}
      </CardTabs.Content>
    </CardTabs.Root>
  );

  function getSubAccounts(AccountId: string, balance: vp.AccountBalance[]) {
    return balance.find((item) => item.AccountID === AccountId)?.SubAccounts || [];
  }
};

export default AccountTabs;
